import config from 'constants/config'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { getAge } from 'lib/datetime/dateUtils'
import { getOptimizelyExperimentVariation, getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import { createSelector } from 'reselect'

export const getDefaultUserDriverCategory = createSelector(
  (state: App.State) => state.auth.account.dob,
  (dateOfBirth): [App.CarHireDriverAgeCategory, number | undefined] => {
    if (dateOfBirth) {
      const years = getAge(dateOfBirth)
      if (years <= 29) {
        return [1, years]
      } else if (years >= 70) {
        return [3, years]
      } else {
        return [2, years]
      }
    }
    // default category
    return [2, undefined]
  },
)

export const isCarHireInsuranceAvailable = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (currentRegionCode) => config.CARHIRE_INSURANCE_ENABLED &&
  config.CARHIRE_INSURANCE_REGIONS.split(',').includes(currentRegionCode),
)

export const isBundledCarHireEnabled = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.carHireBundledDiscount),
  (currentRegionCode, featureEnabled) =>
    config.CARHIRE_ENABLED &&
    isEnabledForFeature(config.CARHIRE_REGIONS, currentRegionCode) &&
    config.BUNDLED_CAR_HIRE_ENABLED &&
    featureEnabled &&
    config.businessTraveller.currentAccountMode !== 'business',
)

export const getCartItemsEligibleForBundledCarHire = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (cartItems, offers): Array<App.Checkout.InstantBookingLEHotelItem> => {
    return cartItems
      // separate filter so typescript gets happy with the return type
      .filter(item => isInstantBookingLEHotelItem(item))
      // We currently only support bundled car hire for LPC
      .filter(item => isLpcHotelOffer(offers[item.offerId]) && item.checkIn && item.checkOut)
  },
)

export const isCartEligibleForBundledCarHire = createSelector(
  (state: App.State) => getCartItemsEligibleForBundledCarHire(state),
  (cartItemsEligibleForBundledCarHire) => cartItemsEligibleForBundledCarHire.length > 0,
)

export const isCarHireBundledDiscountHomepageCarouselEnabled = createSelector(
  (state: App.State) => isBundledCarHireEnabled(state),
  (state: App.State) => !!getOptimizelyExperimentVariation(state, OptimizelyExperiments.carHireBundledDiscountUpcomingOrderHomepageCarousel),
  (bundledCarHireEnabled, featureEnabled) => bundledCarHireEnabled && featureEnabled,
)
